import './PaypalECS.css';

import type {AppConfig} from '../initialize';
import type {
  ButtonStyle,
  IntegrationMethod,
  OnClickActions,
  PaypalECSInboundPayload,
} from '../../shared/PaypalECSTypes';
import {initialize} from '../initialize';
import type {RequestFn, SendFn} from '../setupMessages';
import injectScript from './injectScript';
import {getSdk} from './paypalSdk';

let isButtonLoaded: boolean;

// client-id changes dynamically, so passing an integration date ensures that no breaking changes are made to this integration
// https://developer.paypal.com/sdk/js/configuration/#integration-date
const INTEGRATION_DATE = '2022-09-15';

type UrlParams = {
  'client-id': string;
  intent: string;
  currency: string;
  [key: string]: string;
};

const initializeButton = (
  urlParams: UrlParams,
  integrationMethod: IntegrationMethod,
  send: SendFn<'PaypalECS'>,
  request: RequestFn<'PaypalECS'>,
  style?: ButtonStyle,
): void => {
  if (isButtonLoaded) {
    return;
  }

  let url = `https://www.paypal.com/sdk/js?components=buttons&integration-date=${INTEGRATION_DATE}`;
  Object.keys(urlParams).forEach((param) => {
    url += `&${param}=${encodeURIComponent(urlParams[param])}`;
  });

  const handleError = (err: Record<string, unknown>): void => {
    send({
      event: 'onError',
      error: (err as unknown as Error).message,
    });
  };

  const handleInit = (): void => {
    send({
      event: 'onInit',
    });
  };

  const handleClick = (
    _: Record<string, unknown>,
    actions: OnClickActions,
  ): void | Promise<void> => {
    return request({
      event: 'onClick',
    }).then((response) => {
      if (response.event === 'rejectClick') {
        return actions.reject();
      }

      return actions.resolve();
    });
  };

  const handleApprove = (): Promise<void> => {
    send({
      event: 'onApprove',
    });
    return Promise.resolve();
  };

  const handleCancel = (): Promise<void> => {
    send({
      event: 'onCancel',
    });
    return Promise.resolve();
  };

  injectScript(
    url,
    () => {
      isButtonLoaded = true;
      const paypal = getSdk();
      if (!paypal || !paypal.Buttons || !paypal.FUNDING) {
        return;
      }

      if (integrationMethod === 'createOrder') {
        paypal
          .Buttons({
            fundingSource: paypal.FUNDING.PAYPAL,
            style,
            // Sets up the transaction when a payment button is clicked
            createOrder: (data, action) => {
              return request({
                event: 'createOrder',
              })
                .then((resp) => {
                  if (resp.event === 'setOrder') {
                    return Promise.resolve(resp.orderId);
                  }

                  if (resp.event === 'createOrderClientSide') {
                    return action.order.create({
                      purchase_units: [
                        {
                          amount: {
                            currency_code: resp.amount.currency,
                            value: resp.amount.amount,
                          },
                        },
                      ],
                    });
                  }

                  throw Error(
                    `Expected 'setOrder' or 'createOrderClientSide' action in response, got '${resp.event}'`,
                  );
                })
                .then((orderId) => orderId);
            },
            onError: handleError,
            onInit: handleInit,
            onClick: handleClick,
            onApprove: handleApprove,
            onCancel: handleCancel,
            onShippingChange: (data, actions) => {
              const selectedShippingOption = data?.selected_shipping_option
                ? {
                    // @ts-expect-error - This does appear on the response, just not in the TS types
                    // eslint-disable-next-line @stripe-internal/stripe/no-props-suppress-errors
                    id: data.selected_shipping_option.id,
                    label: data.selected_shipping_option.label,
                    type: data.selected_shipping_option.type.toLowerCase() as
                      | 'shipping'
                      | 'pickup',
                    amount: {
                      currency:
                        data.selected_shipping_option.amount.currency_code,
                      amount: data?.selected_shipping_option.amount.value,
                    },
                  }
                : undefined;

              return request({
                event: 'onShippingChange',
                orderId: data?.orderID,
                shippingAddress: {
                  city: data?.shipping_address?.city,
                  state: data?.shipping_address?.state,
                  countryCode: data?.shipping_address?.country_code,
                  postalCode: data?.shipping_address?.postal_code,
                },
                selectedShippingOption,
              }).then((response) => {
                if (response.event === 'rejectShipping') {
                  return actions.reject();
                }

                return actions.resolve();
              });
            },
          })
          .render('#paypal-button-container');
      } else if (integrationMethod === 'createBillingAgreement') {
        paypal
          .Buttons({
            fundingSource: paypal.FUNDING.PAYPAL,
            style,
            createBillingAgreement: () => {
              return request({
                event: 'createBillingAgreement',
              })
                .then((resp) => {
                  if (resp.event === 'setBillingAgreementToken') {
                    return Promise.resolve(resp.billingAgreementToken);
                  }
                  throw Error(
                    `Expected 'setBillingAgreementToken' action in response, got '${resp.event}'`,
                  );
                })
                .then((billingAgreementToken) => billingAgreementToken);
            },
            onError: handleError,
            onInit: handleInit,
            onClick: handleClick,
            onApprove: handleApprove,
            onCancel: handleCancel,
          })
          .render('#paypal-button-container');
      } else {
        throw new Error(
          `Integration method '${integrationMethod}' is not supported`,
        );
      }
    },
    () => {
      send({
        event: 'onError',
        error: `Error loading PayPal script ${url}`,
      });
    },
  );
};

const initializeButtonV6 = (
  messageData: PaypalECSInboundPayload & {event: 'loadButton'},
  integrationMethod: IntegrationMethod,
  send: SendFn<'PaypalECS'>,
  request: RequestFn<'PaypalECS'>,
): void => {
  if (isButtonLoaded) {
    return;
  }

  const v6ScriptUrl = messageData.isLivemode
    ? 'https://www.paypal.com/web-sdk/v6/core'
    : 'https://www.sandbox.paypal.com/web-sdk/v6/core';

  const handleError = (err: any): void => {
    send({
      event: 'onError',
      error: err.message || 'PayPal error',
    });
  };

  const handleInit = (): void => {
    send({
      event: 'onInit',
    });
  };

  const handleApprove = (): Promise<void> => {
    send({
      event: 'onApprove',
    });
    return Promise.resolve();
  };

  const handleCancel = (): Promise<void> => {
    send({
      event: 'onCancel',
    });
    return Promise.resolve();
  };

  const handleComplete = (): Promise<void> => {
    return Promise.resolve();
  };

  injectScript(
    v6ScriptUrl,
    async () => {
      isButtonLoaded = true;
      if (!window.paypal || !(window as any).paypal.createInstance) {
        throw new Error('PayPal v6 SDK not properly loaded');
      }

      const paypal = await (window as any).paypal.createInstance({
        clientToken: messageData.clientToken!,
        components:
          integrationMethod === 'createBillingAgreement'
            ? ['paypal-legacy-billing-agreements']
            : ['paypal-payments'],
      });

      const createOnClickHandler = (
        session: any,
        validateAndCreateFunction: () => Promise<any>,
        shouldUsePaymentHandler: boolean,
      ) => {
        return async () => {
          try {
            if (shouldUsePaymentHandler) {
              try {
                await session.start(
                  {presentationMode: 'payment-handler'},
                  validateAndCreateFunction(),
                );
              } catch (error: any) {
                await session.start(
                  {presentationMode: 'auto'},
                  validateAndCreateFunction(),
                );
              }
            } else {
              await session.start(
                {presentationMode: 'auto'},
                validateAndCreateFunction(),
              );
            }
          } catch (error) {
            handleError(error);
          }
        };
      };

      let onClick: () => Promise<void>;

      if (integrationMethod === 'createOrder') {
        const paypalOneTimePaymentSession =
          paypal.createPayPalOneTimePaymentSession({
            onApprove: handleApprove,
            onCancel: handleCancel,
            onError: handleError,
            onShippingAddressChange: async (data: any) => {
              const response = await request({
                event: 'onShippingChange',
                orderId: data.orderId,
                shippingAddress: {
                  city: data.shippingAddress?.city,
                  state: data.shippingAddress?.state,
                  countryCode: data.shippingAddress?.countryCode,
                  postalCode: data.shippingAddress?.postalCode,
                },
                selectedShippingOption: undefined,
              });

              if (response.event === 'rejectShipping') {
                throw new Error(data.errors.ADDRESS_ERROR);
              }
              return Promise.resolve();
            },
            onShippingOptionsChange: async (data: any) => {
              const response = await request({
                event: 'onShippingChange',
                orderId: data.orderId,
                shippingAddress: {
                  city: undefined,
                  state: undefined,
                  countryCode: undefined,
                  postalCode: undefined,
                },
                selectedShippingOption: {
                  id: data.selectedShippingOption?.id,
                  label: data.selectedShippingOption?.label,
                  type: data.selectedShippingOption?.type?.toLowerCase() as
                    | 'shipping'
                    | 'pickup',
                  amount: {
                    currency: data.selectedShippingOption?.amount?.currencyCode,
                    amount: data.selectedShippingOption?.amount?.value,
                  },
                },
              });

              if (response.event === 'rejectShipping') {
                throw new Error(data.errors.METHOD_UNAVAILABLE);
              }
              return Promise.resolve();
            },
          });

        const validateAndCreateOrder = async () => {
          const clickResponse = await request({event: 'onClick'});
          if (clickResponse.event === 'rejectClick') {
            return;
          }

          const resp = await request({
            event: 'createOrder',
          });

          if (resp.event === 'setOrder') {
            return {orderId: resp.orderId};
          }

          throw Error(
            `Expected 'setOrder' action in response, got '${resp.event}'`,
          );
        };

        onClick = createOnClickHandler(
          paypalOneTimePaymentSession,
          validateAndCreateOrder,
          messageData.shouldUsePaymentHandler || false,
        );
      } else if (integrationMethod === 'createBillingAgreement') {
        const paypalBillingAgreementSession =
          paypal.createPayPalBillingAgreementWithoutPurchase({
            onApprove: handleApprove,
            onCancel: handleCancel,
            onError: handleError,
            onComplete: handleComplete,
          });

        const validateAndCreateBillingAgreement = async () => {
          // First, validate the click with Stripe (this runs inside the popup loading screen)
          const clickResponse = await request({event: 'onClick'});
          if (clickResponse.event === 'rejectClick') {
            return;
          }

          // Then create the billing agreement
          const resp = await request({
            event: 'createBillingAgreement',
          });

          if (resp.event === 'setBillingAgreementToken') {
            return {billingToken: resp.billingAgreementToken};
          }

          throw Error(
            `Expected 'setBillingAgreementToken' action in response, got '${resp.event}'`,
          );
        };

        onClick = createOnClickHandler(
          paypalBillingAgreementSession,
          validateAndCreateBillingAgreement,
          false, // billing agreements don't support payment handler in PayPal v6 SDK
        );
      } else {
        throw new Error(
          `Integration method '${integrationMethod}' is not supported in v6`,
        );
      }

      const paypalButton = document.createElement('paypal-button');

      const colorClass = messageData.style?.color
        ? `paypal-${messageData.style.color}`
        : 'paypal-gold';
      paypalButton.setAttribute('class', colorClass);

      if (messageData.style?.label) {
        paypalButton.setAttribute('type', messageData.style.label);
      }

      paypalButton.addEventListener('click', onClick);

      const container = document.getElementById('paypal-button-container');
      if (!container) {
        throw new Error('PayPal button container not found');
      }
      container.appendChild(paypalButton);

      handleInit();
    },
    () =>
      send({
        event: 'onError',
        error: `Error loading PayPal v6 script`,
      }),
  );
};

const app = (config: AppConfig<'PaypalECS'>) => {
  config.subscribe((messageData) => {
    if (messageData.event === 'loadButton') {
      const integrationMethod: IntegrationMethod =
        messageData.integrationMethod || 'createOrder';

      // V6 usage is gated by the presence of a clientToken
      const useV6Sdk = !!messageData.clientToken;

      if (useV6Sdk) {
        initializeButtonV6(
          messageData,
          integrationMethod,
          config.send,
          config.request,
        );
      } else {
        const params: UrlParams = {
          'client-id': messageData.clientId,
          intent: messageData.intent,
          currency: messageData.currency,
        };

        if (messageData.merchantId) {
          params['merchant-id'] = messageData.merchantId;
        }
        if (messageData.locale) {
          params.locale = messageData.locale;
        }
        if (messageData.vault) {
          params.vault = messageData.vault.toString();
        }

        initializeButton(
          params,
          integrationMethod,
          config.send,
          config.request,
          messageData.style,
        );
      }
    }
  });
};

initialize({
  app,
  owner: 'checkout',
});
